import { json, redirect } from "@remix-run/node";
import { isPreLaunch, hasPreviewAccess } from "~/utils/launch";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { originFromRequest } from "~/utils/meta";
import { Outlet, Link, useLocation, useNavigation, useLoaderData } from "@remix-run/react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import { usePageviewTracker } from "~/hooks/usePageviewTracker";
import storefrontCss from "~/styles/storefront.css?url";
import prisma from "~/utils/db.server";
import { FREE_SHIPPING_THRESHOLD, fixImgUrl } from "~/utils/storefront-constants";
import { formatStorefrontPrice } from "~/utils/money";
import { detectLocale, localePath, parseAcceptLanguage } from "~/utils/locale";
import type { Locale } from "~/utils/locale";
import { t, getAnnouncements } from "~/utils/translations";
import { cacheWrap } from "~/utils/cache.server";
import { resolveMarket, createMarketCookie } from "~/services/markets/marketDetection.server";
import { optionalRetailAuth } from "~/services/auth/retailSession.server";
import { getMembershipConfig } from "~/services/membership/membershipService.server";
import { signCustomerToken } from "~/services/messenger/customerToken.server";
import { getTierDef, type TierName } from "~/services/rewards/rewardsTiers";
import {
  loadSearchHistory,
  saveSearchHistory,
  removeSearchHistoryEntry,
  clearSearchHistory,
} from "~/utils/searchHistory";
import { displayTitle } from "~/utils/productTitle";
import { getOrCreateRetailSessionId } from "~/utils/retailSession";
import { useNonce } from "~/utils/nonce-provider";
import { CookieConsent } from "~/components/CookieConsent";
import { LocaleSelectModal } from "~/components/storefront/LocaleSelectModal";
import { PhoneCaptureModal } from "~/components/storefront/PhoneCaptureModal";
import { SocialIcon, hasSocialIcon } from "~/components/storefront/SocialIcon";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: storefrontCss },
  // Preload the A4 Speed brand face (3.9KB) — the DIOLO wordmark is
  // above-the-fold, so preloading lets it paint in-brand on first render
  // instead of swapping ~100ms later. Storefront-only (this layout doesn't
  // wrap admin), so admin pages never fetch it.
  {
    rel: "preload",
    as: "font",
    type: "font/woff2",
    href: "/fonts/A4Speed-Bold.woff2",
    crossOrigin: "anonymous",
  },
];

// Organization JSON-LD is emitted as an inline <script> in the layout
// component body (not via a meta export) because Remix v2 child route
// meta REPLACES parent meta — exporting Organization here would only
// fire on routes without their own meta function. The body <script>
// always renders since the layout wraps every storefront page.
// Implementation lives at the bottom of StorefrontLayout near <Outlet />.

// ─── Loader: fetch recommendations, locale, market, theme data ─── //
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const reqCookies = request.headers.get("Cookie") || "";

  // ─── Pre-launch takeover (until 21:00 on 09.09) ───
  // Every storefront route — home, PDPs, cart, checkout, search — bounces to
  // the /001 teaser unless this browser holds the preview cookie from
  // /access?k=… . Thrown here, in the layout loader, so no child loader's
  // catalog data ever reaches the client. Lifts itself at LAUNCH_AT.
  if (isPreLaunch() && !hasPreviewAccess(reqCookies)) {
    throw redirect("/001");
  }
  const locale = detectLocale(request.url, reqCookies);
  const tawktoPropertyId = process.env.TAWKTO_PROPERTY_ID || null;
  const vapidPublicKey = process.env.VAPID_PUBLIC_KEY || null;

  const shop = await cacheWrap(
    "shop",
    () => prisma.shop.findFirst({
      select: {
        id: true,
        currency: true,
        themeColors: true,
        themeFonts: true,
        heroSlides: true,
        announcementBar: true,
        socialLinks: true,
        footerText: true,
      },
    }),
    { ttl: 60 }
  );

  if (!shop) {
    return json({
      recommendations: [],
      currency: "VND",
      locale,
      // True for holders of the (HttpOnly) access cookie — the list's own
      // people. Client code can't read that cookie, so it's decided here.
      previewAccess: hasPreviewAccess(reqCookies),
      retailCustomer: null,
      market: null,
      suggestedMarket: null,
      showMarketSuggestion: false,
      markets: [],
      themeColors: null,
      themeFonts: null,
      heroSlides: null,
      announcementBar: null,
      socialLinks: null,
      footerText: null,
      mainMenu: null,
      footerMenu: null,
      membership: null,
      tawktoPropertyId,
      vapidPublicKey,
      effectiveFreeShippingThreshold: null,
      origin: originFromRequest(request),
      // Match main-return shape — fresh-install fallback when no shop
      // exists yet. The modal is suppressed via `localeChosen: true`
      // since the site has no real content to localize anyway, and the
      // operator likely just hit /admin/setup.
      localeChosen: true,
      suggestedLocale: locale,
    });
  }

  const auth = await optionalRetailAuth(request);
  let retailCustomer: {
    firstName: string;
    email: string;
    rewardsTier: TierName | null;
    /** Short-lived HMAC token authorising the Messenger webhook to
     *  link the resulting PSID back to this customer when they click
     *  the PDP "Message us" button. Server-side signed (JWT_SECRET +
     *  1h expiry). Embedded in the m.me ref payload, validated by
     *  the webhook via verifyCustomerToken. Null for anonymous
     *  visitors — they fall back to the phone-regex matching path. */
    messengerLinkToken: string | null;
  } | null = null;
  if (auth) {
    const rewards = await prisma.rewardsAccount.findUnique({
      where: { customerId: auth.customer.id },
      select: { tier: true },
    });
    // Sign the link token lazily — if JWT_SECRET is unset we fail
    // closed (the rest of auth requires it anyway), but we don't want
    // a one-off issue here to take down the whole storefront layout.
    let messengerLinkToken: string | null = null;
    try {
      messengerLinkToken = signCustomerToken(auth.customer.id);
    } catch (err) {
      // Sentry breadcrumb but don't fail the request — anonymous
      // path (phone-regex matcher) still works for the customer.
      console.warn("[_storefront] signCustomerToken failed:", err);
    }
    retailCustomer = {
      firstName: auth.customer.firstName ?? "",
      email: auth.customer.email,
      rewardsTier: (rewards?.tier ?? null) as TierName | null,
      messengerLinkToken,
    };
  }

  const { market, suggestedMarket, showSuggestion } = await resolveMarket(request, shop.id);

  // ─── Effective free-shipping threshold (tier-aware) ─── //
  // Logged-in customers on higher tiers get a lower (or zero) free-shipping
  // threshold than the market default. This is the single source of truth
  // used by the cart, checkout, mini-cart, and announcement bar.
  const marketCurrency = market?.currency || shop.currency || "VND";
  const isVietnamMarket = marketCurrency === "VND";
  // Bug fix (2026-05-08): the previous fallback used FREE_SHIPPING_THRESHOLD
  // (1_500_000 — a VND constant) for any market without an explicit
  // override. On non-VND markets, that VND value got rendered with the
  // active currency symbol — so an intl visitor saw e.g. "$1,500,000.00"
  // for the free-shipping line, treating the VND threshold as USD.
  // Same problem applies to the per-tier thresholds below
  // (tierDef.freeShippingIntlThresholdVND is also VND-denominated).
  //
  // Fix policy (option C from the audit notes): only render a threshold
  // when we have one denominated in the customer's actual market currency.
  //   - VN market: threshold is always in VND, render normally.
  //   - Non-VN market with explicit market.freeShippingThreshold: that
  //     value is per-market, already in market currency — render it.
  //   - Non-VN market without explicit threshold: set to `null` so the
  //     UI falls back to "Shipping thresholds apply at checkout"
  //     (which the PDP, cart, mini-cart, and bar already handle).
  // Tier-based thresholds are only applied on the VN market for the
  // same reason — the constants are explicitly VND-denominated.
  let effectiveFreeShippingThreshold: number | null = null;
  if (isVietnamMarket) {
    const baseFreeShippingThreshold = market?.freeShippingThreshold
      ? Number(market.freeShippingThreshold)
      : FREE_SHIPPING_THRESHOLD;
    effectiveFreeShippingThreshold = baseFreeShippingThreshold;
    if (retailCustomer?.rewardsTier) {
      const tierDef = getTierDef(retailCustomer.rewardsTier);
      const tierThreshold = tierDef.freeShippingVNThresholdVND;
      if (tierThreshold === null) {
        effectiveFreeShippingThreshold = 0; // always free for this tier
      } else if (typeof tierThreshold === "number" && tierThreshold > 0) {
        effectiveFreeShippingThreshold = Math.min(
          baseFreeShippingThreshold,
          tierThreshold,
        );
      }
    }
  } else if (market?.freeShippingThreshold) {
    // Non-VN market with an explicit threshold the merchant has
    // configured in market currency. Honour it as-is; do NOT layer the
    // VND-denominated tier overrides on top.
    effectiveFreeShippingThreshold = Number(market.freeShippingThreshold);
  }
  // else: leave null — UI will say "Shipping thresholds apply at checkout".

  // Membership program: free-shipping-forever (40-stamp perk) zeroes the
  // threshold everywhere the storefront reads it (cart bar, checkout).
  if (isVietnamMarket && auth) {
    const member = await prisma.membershipAccount
      .findUnique({ where: { customerId: auth.customer.id }, select: { freeShippingForever: true } })
      .catch(() => null);
    if (member?.freeShippingForever) effectiveFreeShippingThreshold = 0;
  }

  // Lifetime Set Membership (config-gated, docs/cissie/membership-plan.md):
  // the client needs to know the program is on (account nav tab, PDP/cart
  // "+1 stamp" hints) and which products DON'T earn stamps so the cart can
  // preview "+N" the same way checkout does. Null = program off = every
  // membership surface renders nothing.
  let membership: {
    enabled: boolean;
    programName: string;
    unitLabel: { vi: string; en: string };
    nonStampProductIds: string[];
  } | null = null;
  try {
    const mcfg = await getMembershipConfig();
    if (mcfg.enabled) {
      const nonStamp = await cacheWrap(
        `storefront:membership:non-stamp:${shop.id}`,
        () => prisma.product.findMany({ where: { shopId: shop.id, earnsStamps: false }, select: { id: true } }),
        { ttl: 60 },
      );
      membership = {
        enabled: true,
        programName: mcfg.programName,
        unitLabel: mcfg.unitLabel,
        nonStampProductIds: nonStamp.map((p) => p.id),
      };
    }
  } catch (e) {
    console.error("[storefront] membership context load failed:", e);
  }

  const hasLocaleCookie = reqCookies.includes("eql_locale=");
  // Presence of `eql_locale_chosen` cookie = user has made an explicit
  // language choice (via the LocaleSelectModal first-visit popup OR the
  // in-header switcher). When absent, the popup renders to gather a
  // confirmed choice. Server-side gating (here) prevents flash-of-modal
  // for returning visitors and ensures bots/SSR consumers never see it.
  // (2026-05-11: replaces the previous silent server-side auto-redirect
  // to English — customer feedback was that an explicit picker is better
  // UX, especially for bilingual visitors and customers browsing from a
  // country different from where they ship.)
  const localeChosen = /(?:^|;\s*)eql_locale_chosen=true/.test(reqCookies);

  // Compute the locale to PRE-HIGHLIGHT in the modal as "Recommended".
  // Mirrors the previous auto-redirect heuristic: market language >
  // suggested-market language > Accept-Language q-weights > vi default.
  // No redirect happens — the user still has to click to confirm.
  let suggestedLocale: Locale = "vi";
  if (market?.language === "en") {
    suggestedLocale = "en";
  } else if (suggestedMarket?.language === "en" && showSuggestion) {
    suggestedLocale = "en";
  } else {
    const acceptLang = request.headers.get("Accept-Language") || "";
    const preferred = parseAcceptLanguage(acceptLang);
    if (preferred === "en") suggestedLocale = "en";
  }

  const [recommendations, markets, mainMenu, footerMenu] = await Promise.all([
    cacheWrap(
      `recs:${shop.id}`,
      () => prisma.product.findMany({
        where: {
          shopId: shop.id,
          isRetailEnabled: true,
          status: "ACTIVE",
          retailPrice: { gt: 0 },
          featuredImageUrl: { not: null },
          variants: { some: { inventoryQuantity: { gt: 0 } } },
        },
        select: {
          id: true,
          handle: true,
          title: true,
          titleEn: true,
          featuredImageUrl: true,
          retailPrice: true,
          activity: true,
          productType: true,
          variants: { select: { id: true, price: true, compareAtPrice: true }, take: 1 },
        },
        orderBy: { updatedAt: "desc" },
        take: 50,
      }),
      { ttl: 30 }
    ),
    cacheWrap(
      `markets:${shop.id}`,
      () => prisma.market.findMany({
        where: { shopId: shop.id, isActive: true },
        select: { id: true, name: true, currency: true, language: true, countries: true, freeShippingThreshold: true, flatShippingRate: true },
        orderBy: { isPrimary: "desc" },
      }),
      { ttl: 300 }
    ),
    cacheWrap(
      `mainMenu:${shop.id}`,
      () => prisma.navigationMenu.findUnique({
        where: { shopId_handle: { shopId: shop.id, handle: "main-menu" } },
        include: {
          items: {
            where: { parentId: null },
            orderBy: { position: "asc" },
            include: { children: { orderBy: { position: "asc" } } },
          },
        },
      }),
      { ttl: 300 }
    ),
    cacheWrap(
      `footerMenu:${shop.id}`,
      () => prisma.navigationMenu.findUnique({
        where: { shopId_handle: { shopId: shop.id, handle: "footer-menu" } },
        include: {
          items: {
            where: { parentId: null },
            orderBy: { position: "asc" },
            include: { children: { orderBy: { position: "asc" } } },
          },
        },
      }),
      { ttl: 300 }
    ),
  ]);

  // Market pricing for recommendations
  if (market && recommendations.length > 0) {
    try {
      const { getMarketPrices } = await import("~/services/markets/marketPricing.server");
      const recVariants = recommendations
        .filter((r: any) => r.variants?.[0])
        .map((r: any) => ({
          id: r.variants[0].id,
          price: Number(r.variants[0].price),
          compareAtPrice: r.variants[0].compareAtPrice ? Number(r.variants[0].compareAtPrice) : null,
        }));

      if (recVariants.length > 0) {
        const mpMap = await getMarketPrices(recVariants, market.marketId, shop.currency || "VND", shop.id);
        for (const r of recommendations as any[]) {
          if (r.variants?.[0]) {
            const mp = mpMap.get(r.variants[0].id);
            if (mp) r.retailPrice = mp.price;
          }
        }
      }
    } catch (e) {
      // Market pricing is non-critical — proceed without it
    }
  }

  const headers: Record<string, string> = {};
  const setCookies: string[] = [];
  if (market && !reqCookies.includes("eql_market=")) {
    setCookies.push(createMarketCookie(market.marketId));
  }
  if (!hasLocaleCookie) {
    setCookies.push(`eql_locale=${locale};path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax;Secure`);
  }
  if (setCookies.length > 0) {
    headers["Set-Cookie"] = setCookies.join(", ");
  }

  return json(
    {
      recommendations,
      currency: market?.currency || shop.currency || "VND",
      locale,
      previewAccess: hasPreviewAccess(reqCookies),
      retailCustomer,
      market,
      suggestedMarket,
      showMarketSuggestion: showSuggestion,
      markets,
      themeColors: shop.themeColors,
      themeFonts: shop.themeFonts,
      heroSlides: shop.heroSlides,
      announcementBar: shop.announcementBar,
      socialLinks: shop.socialLinks,
      footerText: shop.footerText,
      mainMenu,
      footerMenu,
      membership,
      tawktoPropertyId,
      vapidPublicKey,
      effectiveFreeShippingThreshold,
      origin: originFromRequest(request),
      // First-visit locale picker — modal renders when `localeChosen` is
      // false. `suggestedLocale` is pre-highlighted as the recommended
      // choice (mirrors the previous server-side redirect heuristic).
      localeChosen,
      suggestedLocale,
    },
    { headers }
  );
};

// ─── Cart Context (cookie-based) ─── //
export interface CartItem {
  variantId: string;
  productHandle: string;
  productTitle: string;
  /**
   * Optional curated English title from the Sapo product row. When set,
   * `displayTitle()` prefers it over the regex-strip fallback so EN
   * customers see the clean English form even on titles like
   * "Focus - Black (Đen) - Jacket/Áo khoác" where the trailing slash
   * pattern can't be parsed safely. Items added before this field
   * existed will fall back to the regex strip, which handles the
   * common "Brand - Color (Vietnamese)" pattern.
   */
  productTitleEn?: string | null;
  variantTitle: string;
  color: string;
  size: string;
  price: number;
  comparePrice?: number;
  imageUrl?: string;
  quantity: number;
  currency?: string; // Market currency at time of add (e.g., "VND", "USD")
  productId?: string; // For server-side price verification
  /**
   * Inventory cap for THIS variant at the moment it was added to the
   * cart. Cart-side controls (addToCart, updateQuantity, checkout
   * +/− buttons) refuse to exceed this number so a customer can't
   * add 12 of an item where only 3 are in stock. May be undefined
   * for cart entries from before this field existed; callers treat
   * undefined as "no client-side cap" and fall back to the
   * server-side inventory check at order creation. (Audit
   * 2026-04-25.)
   */
  maxQuantity?: number;
}

function getCartFromCookie(): CartItem[] {
  if (typeof document === "undefined") return [];
  try {
    const match = document.cookie.match(/(?:^|;\s*)eql_cart=([^;]*)/);
    if (!match) return [];
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return [];
  }
}

function saveCartToCookie(items: CartItem[]) {
  const val = encodeURIComponent(JSON.stringify(items));
  // Audit 2026-04-30: added Secure flag. The site is HSTS-enforced,
  // but the explicit Secure flag is still good practice and is
  // browser-ignored if loaded over HTTP (so it's safe even in dev).
  document.cookie = `eql_cart=${val};path=/;Secure;max-age=${60 * 60 * 24 * 30};SameSite=Lax`;
}

// ─── Mega Menu Activities & Types (bilingual) ─── //
// hrefs use the canonical DB activity values (court/studio/train/lounge)
// so the category-strip "active state" on the products page highlights
// correctly. The /products loader also accepts legacy tennis/yoga/gym
// aliases for SEO + bookmarks, but internal nav should prefer the
// canonical values. (Audit finding P1.4)
const MEGA_MENU_ACTIVITIES = [
  { key: "train", vi: "Tập Luyện", en: "Train", href: "/products?activity=train" },
  { key: "court", vi: "Sân Đấu", en: "Court", href: "/products?activity=court" },
  { key: "run", vi: "Chạy Bộ", en: "Run", href: "/products?activity=run" },
  { key: "lounge", vi: "Ngày Nghỉ", en: "Lounge", href: "/products?activity=lounge" },
];

// "By Type" mega-menu links. Bug fix (2026-05-08): previously these
// linked to /products?tag=<type>, but the `tag` URL param does a
// substring match on Product.tags (which holds curation flags like
// "new", "sale", "best-seller") — NOT productType / piece-keyword.
// So every "By Type" link rendered "0 PRODUCTS / No products found"
// on the storefront. Now they use ?type=<group> which the products
// loader maps to a TITLE-KEYWORD whitelist (Tops = bra/tank/top/...
// Bottoms = skirt/legging/short/..., etc.) — see TYPE_GROUP_KEYWORDS
// in app/routes/_storefront.products._index.tsx.
// Menswear taxonomy (2026-06-12 rebrand): links target the exact
// Product.productType via ?category=<value> (the /products loader maps
// `category` straight to productType), so no keyword-group indirection
// is needed for the four launch types.
const MEGA_MENU_TYPES = [
  { key: "tanks", vi: "Áo Tank", en: "Tank Tops", href: "/products?category=Tank%20Tops" },
  { key: "tees", vi: "Áo Thun", en: "T-Shirts", href: "/products?category=T-Shirts" },
  { key: "polos", vi: "Áo Polo", en: "Polos", href: "/products?category=Polo%20Shirts" },
  { key: "shorts", vi: "Quần Short", en: "Shorts", href: "/products?category=Shorts" },
];

const MEGA_MENU_QUICK_LINKS = (locale: string) => [
  { label: locale === "en" ? "New Arrivals" : "Hàng mới về", href: "/products?tag=new" },
  { label: locale === "en" ? "Bestsellers" : "Bán chạy nhất", href: "/products?sort=best-selling" },
  { label: locale === "en" ? "All Products" : "Tất cả sản phẩm", href: "/products" },
  { label: locale === "en" ? "Sale" : "Giảm giá", href: "/products?tag=sale" },
  { label: locale === "en" ? "Lookbook" : "Lookbook", href: "/lookbook" },
];

// ─── Push Notification Prompt Component ─── //
function PushNotificationPrompt({ vapidPublicKey, locale = "vi" }: { vapidPublicKey: string | null; locale?: string }) {
  const [show, setShow] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (!vapidPublicKey) return;
    if (typeof window === "undefined") return;

    // T4.13: stricter re-ask policy than a flat 7-day cooldown.
    //
    // Rules:
    //  - If the browser permission is "denied" OR "granted", never prompt.
    //  - If the user has dismissed our custom Diolo prompt ≥3 times in the
    //    last 90 days, back off permanently — they've signaled disinterest.
    //    The count is stored alongside the timestamp as `count|ts`.
    //  - Otherwise, honor the 7-day cooldown after the last dismissal.
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    if (Notification.permission !== "default") return; // granted or denied → never ask again

    try {
      const raw = localStorage.getItem("eql_push_dismissed");
      if (raw) {
        // Parse "count|ts" format; fall back to legacy "ts" format
        let count = 1;
        let dismissedAt = 0;
        if (raw.includes("|")) {
          const [c, t] = raw.split("|");
          count = parseInt(c, 10) || 1;
          dismissedAt = parseInt(t, 10) || 0;
        } else {
          dismissedAt = parseInt(raw, 10) || 0;
        }
        // Permanent back-off after 3+ dismissals
        if (count >= 3) return;
        // 7-day cooldown after last dismissal
        if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
      }
    } catch {}

    const timer = setTimeout(() => setShow(true), 30000);
    return () => clearTimeout(timer);
  }, [vapidPublicKey]);

  // T4.13: shared write helper that increments the dismissal count
  const recordDismissal = () => {
    try {
      const raw = localStorage.getItem("eql_push_dismissed");
      let count = 0;
      if (raw?.includes("|")) count = parseInt(raw.split("|")[0], 10) || 0;
      else if (raw) count = 1; // legacy format had no count; assume 1 prior dismissal
      localStorage.setItem("eql_push_dismissed", `${count + 1}|${Date.now()}`);
    } catch {}
  };

  const handleEnable = async () => {
    if (!vapidPublicKey) return;
    setSubscribing(true);
    try {
      // T4.12: gracefully skip if the service worker isn't available.
      // `register()` rejects on 404, CSP violation, or some mobile
      // browsers in private mode. Swallowing the error and still trying
      // to subscribe would throw later inside pushManager.subscribe.
      if (!("serviceWorker" in navigator)) {
        throw new Error("Service worker not supported");
      }
      const registration = await navigator.serviceWorker.register("/sw.js").catch((err) => {
        console.warn("[push] Service worker registration failed:", err);
        return null;
      });
      if (!registration) throw new Error("Service worker registration failed");

      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidPublicKey,
        });
        await fetch("/api/push-subscribe", {
          method: "POST",
          body: JSON.stringify(subscription),
          headers: { "Content-Type": "application/json" },
        });
      }
    } catch (err) {
      console.warn("[push] Enable flow failed:", err);
    }
    setShow(false);
    // T4.13: record this as a "dismissal" even on success so subsequent
    // visits don't re-prompt. `recordDismissal` writes count|timestamp.
    recordDismissal();
  };

  const handleDismiss = () => {
    setShow(false);
    recordDismissal();
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 animate-fade-in" role="alert">
      <div className="bg-white border border-[#e0e0e0] shadow-lg rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#f4f4f4] flex items-center justify-center">
            <svg className="w-4.5 h-4.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-black leading-tight">{locale === "en" ? "Get notified about new drops, restocks & exclusive offers." : "Nhận thông báo về sản phẩm mới, hàng về lại & ưu đãi độc quyền."}</p>
            <p className="text-xs text-[#767676] mt-0.5">{locale === "en" ? "Never miss our latest drops" : "Không bỏ lỡ sản phẩm mới nhất"}</p>
          </div>
          <button onClick={handleDismiss} className="flex-shrink-0 text-[#767676]/60 hover:opacity-70 transition-colors" aria-label="Dismiss">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleEnable}
            disabled={subscribing}
            className="flex-1 text-xs font-medium tracking-wider uppercase py-2 px-3 bg-black text-white hover:bg-black/90 transition-colors rounded disabled:opacity-50"
          >
            {subscribing ? "..." : (locale === "en" ? "Enable Notifications" : "Bật thông báo")}
          </button>
          <button
            onClick={handleDismiss}
            className="text-xs text-[#767676] hover:opacity-70 tracking-wider uppercase py-2 px-3 transition-colors"
          >
            {locale === "en" ? "Not now" : "Để sau"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Rewards Popup Component ─── //

// ─── Exit Intent Popup Component ─── //
function ExitIntentPopup({ locale, onSubmit }: { locale: string; onSubmit: (email: string, source: string) => void }) {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem("eql_exit_popup_shown")) return;
      // Holders of the access cookie came in through the list's own link —
      // asking them to join the list is noise (2026-09-08, launch eve).
      if (document.cookie.includes("diolo_access=")) return;
    } catch {}

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        setShow(true);
        try { sessionStorage.setItem("eql_exit_popup_shown", "1"); } catch {}
        document.removeEventListener("mouseleave", handleMouseLeave);
      }
    };

    // Delay adding the listener so it doesn't fire on first load
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const dismiss = () => setShow(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    onSubmit(email, "exit_intent");
    setSubmitted(true);
    setTimeout(dismiss, 2500);
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[220] transition-opacity" onClick={dismiss} />
      <div className="fixed inset-0 z-[221] flex items-center justify-center p-4">
        <div className="relative bg-white max-w-md w-full shadow-2xl rounded-lg overflow-hidden">
          <button onClick={dismiss} className="absolute top-4 right-4 text-[#767676] hover:opacity-70 z-10" aria-label="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="p-8 sm:p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-rose-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
            <h2 className="font-display text-2xl font-light tracking-normal mb-2">
              {locale === "en" ? "Wait! Get 10% Off" : "Khoan! Nhận Giảm 10%"}
            </h2>
            <p className="text-sm text-[#767676] mb-6">
              {locale === "en"
                ? "Don't leave empty-handed. Get 10% off your first order."
                : "Đừng bỏ lỡ. Nhận ngay giảm 10% cho đơn hàng đầu tiên."}
            </p>
            {submitted ? (
              <div className="py-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-emerald-800">
                  {locale === "en" ? "Check your inbox for the code!" : "Kiểm tra hộp thư để nhận mã!"}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  placeholder={t("emailPlaceholder", locale as Locale)}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full border border-[#e0e0e0] bg-transparent px-4 py-3 text-sm text-center focus:border-black focus:outline-none"
                />
                <button type="submit" className="sf-btn-primary w-full">
                  {locale === "en" ? "Claim 10% Off" : "Nhận giảm 10%"}
                </button>
              </form>
            )}
            <button onClick={dismiss} className="mt-4 text-xs text-[#767676] underline hover:opacity-70">
              {locale === "en" ? "No thanks" : "Không, cảm ơn"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Cart Confirmation Toast Component ─── //
function CartConfirmationToast({
  item,
  locale,
  onDismiss,
  onViewCart,
}: {
  item: CartItem;
  locale: string;
  onDismiss: () => void;
  onViewCart: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="fixed top-4 right-4 z-[70] w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-emerald-100 sf-cart-confirmation-toast">
      <div className="p-4 flex items-start gap-3">
        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-black line-clamp-1">{displayTitle(item.productTitle, locale, item.productTitleEn)}</p>
          <p className="text-xs text-[#767676] mt-0.5">
            {item.size && `${item.size}`}{item.size && item.color ? " / " : ""}{item.color && `${item.color}`}
          </p>
          <p className="text-xs text-emerald-700 font-medium mt-1">
            {locale === "en" ? "Added to bag" : "Da them vao gio"}
          </p>
          <button
            onClick={onViewCart}
            className="text-xs font-medium text-black underline hover:opacity-70 mt-1.5"
          >
            {locale === "en" ? "View Cart" : "Xem gio hang"}
          </button>
        </div>
        <button onClick={onDismiss} className="text-[#767676] hover:opacity-70 flex-shrink-0" aria-label="Dismiss">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Inline Search Component (Desktop) ─── //
function InlineSearchDesktop({
  locale,
  isTransparent,
  searchQuery,
  setSearchQuery,
  searchResults,
  searchLoading,
  searchOpen,
  setSearchOpen,
  inputRef,
  fmtPrice,
  searchHistory,
  onRecordSearch,
  onRemoveHistoryEntry,
  onClearHistory,
  onClose,
}: {
  locale: string;
  isTransparent: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchResults: any[];
  searchLoading: boolean;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  fmtPrice: (amount: number) => string;
  searchHistory: string[];
  onRecordSearch: (q: string) => void;
  onRemoveHistoryEntry: (q: string) => void;
  onClearHistory: () => void;
  onClose: () => void;
}) {
  // Submit on Enter — record the query into history and navigate to /search.
  // Using window.location avoids the need to pass useNavigate down through
  // every prop drill in this large component tree.
  const submitSearch = () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    onRecordSearch(q);
    onClose();
    window.location.href = `/search?q=${encodeURIComponent(q)}`;
  };

  // Show history when the query is empty but the dropdown is open (focus
  // without typing). Show results once the user types ≥ 1 char.
  const showHistory = searchOpen && searchQuery.length === 0 && searchHistory.length > 0;
  const showResults = searchOpen && searchQuery.length > 0;

  return (
    <div className="relative hidden lg:block">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitSearch();
            }
          }}
          placeholder={t("search", locale as Locale)}
          className={`w-48 xl:w-56 text-xs border-b bg-transparent py-1.5 pr-7 pl-1 focus:outline-none transition-colors ${
            isTransparent
              ? "border-white/40 text-white placeholder:text-white/50 focus:border-white"
              : "border-[#e0e0e0] text-black placeholder:text-[#999] focus:border-black"
          }`}
        />
        <svg className={`absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 ${isTransparent ? "text-white/60" : "text-[#767676]"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      </div>
      {/* Search Dropdown — Recent searches OR live results */}
      {(showHistory || showResults) && (
        <div className="absolute top-full right-0 mt-2 w-[340px] bg-white shadow-xl rounded-lg border border-[#e0e0e0] overflow-hidden z-[60]">
          {showHistory ? (
            <div className="max-h-[420px] overflow-y-auto">
              <div className="px-4 pt-3 pb-1.5 flex items-center justify-between border-b border-[#f0f0f0]">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#999]">
                  {locale === "en" ? "Recent Searches" : "Tìm kiếm gần đây"}
                </p>
                <button
                  type="button"
                  onClick={onClearHistory}
                  className="text-[11px] text-[#999] hover:text-black transition-colors"
                >
                  {locale === "en" ? "Clear" : "Xóa"}
                </button>
              </div>
              {searchHistory.map((entry) => (
                <div key={entry} className="flex items-center hover:bg-[#f8f8f8] transition-colors">
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery(entry);
                      onRecordSearch(entry);
                      onClose();
                      window.location.href = `/search?q=${encodeURIComponent(entry)}`;
                    }}
                    className="flex-1 flex items-center gap-3 px-4 py-2.5 text-left"
                  >
                    <svg className="w-3.5 h-3.5 text-[#999] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-black truncate">{entry}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemoveHistoryEntry(entry); }}
                    aria-label={locale === "en" ? `Remove ${entry}` : `Xóa ${entry}`}
                    className="px-3 py-2.5 text-[#999] hover:text-black transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : searchLoading ? (
            <div className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-[#e0e0e0] border-t-black rounded-full animate-spin mx-auto" />
            </div>
          ) : searchResults.length > 0 ? (
            <div className="max-h-[420px] overflow-y-auto">
              <div className="px-4 pt-3 pb-1.5 flex items-center justify-between border-b border-[#f0f0f0]">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#999]">
                  {locale === "en" ? "Products" : "San pham"}
                </p>
                <p className="text-[11px] text-[#999]">{searchResults.length}{searchResults.length >= 6 ? "+" : ""}</p>
              </div>
              {searchResults.map((result: any) => (
                <Link
                  key={result.id}
                  to={`/products/${result.handle || result.id}`}
                  onClick={() => { onRecordSearch(searchQuery); onClose(); }}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#f8f8f8] transition-colors"
                >
                  <div className="w-11 h-14 flex-shrink-0 rounded bg-[#f4f4f4] overflow-hidden">
                    {(result.imageUrl || result.featuredImageUrl) ? (
                      <img src={fixImgUrl(result.imageUrl || result.featuredImageUrl)} alt={displayTitle(result.title, locale, result.titleEn)} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#ccc]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-black line-clamp-1">{displayTitle(result.title, locale, result.titleEn)}</p>
                    <p className="text-sm text-[#767676] mt-0.5">{fmtPrice(Number(result.price || result.retailPrice || 0))}</p>
                  </div>
                </Link>
              ))}
              <Link
                to={`/search?q=${encodeURIComponent(searchQuery)}`}
                onClick={() => { onRecordSearch(searchQuery); onClose(); }}
                className="flex items-center justify-center gap-1.5 text-xs font-medium text-black py-3 border-t border-[#e0e0e0] hover:bg-[#f8f8f8] transition-colors"
              >
                {locale === "en" ? `View all results for "${searchQuery}"` : `Xem tat ca ket qua cho "${searchQuery}"`}
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-sm text-[#767676]">
                {locale === "en" ? `No results for "${searchQuery}"` : `Khong tim thay ket qua cho "${searchQuery}"`}
              </p>
              <p className="text-xs text-[#999] mt-1.5">
                {locale === "en" ? "Try a different search term" : "Thu tu khoa khac"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobile Search Overlay ─── //
function MobileSearchOverlay({
  locale,
  searchQuery,
  setSearchQuery,
  searchResults,
  searchLoading,
  fmtPrice,
  searchHistory,
  onRecordSearch,
  onRemoveHistoryEntry,
  onClearHistory,
  onClose,
}: {
  locale: string;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchResults: any[];
  searchLoading: boolean;
  fmtPrice: (amount: number) => string;
  searchHistory: string[];
  onRecordSearch: (q: string) => void;
  onRemoveHistoryEntry: (q: string) => void;
  onClearHistory: () => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Submit on Enter — record + navigate. Same pattern as desktop.
  const submitSearch = () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    onRecordSearch(q);
    onClose();
    window.location.href = `/search?q=${encodeURIComponent(q)}`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-white">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e0e0e0]">
        <svg className="w-5 h-5 text-[#767676] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitSearch();
            }
          }}
          placeholder={t("search", locale as Locale)}
          className="flex-1 text-sm bg-transparent focus:outline-none text-black"
        />
        <button onClick={onClose} className="text-[#767676] hover:opacity-70" aria-label="Close search">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {/* Audit 2026-05-17: 100vh → 100dvh. iOS Safari collapses its URL
          bar mid-scroll, which shrinks `vh` and clipped the bottom of
          this scroll container — visitors saw their content cut off
          and bounced. `dvh` (dynamic viewport height) tracks the URL-
          bar collapse and resizes correctly. Falls back to 100vh in
          browsers without dvh support (Safari 15.4 and earlier — a
          tiny share of VN traffic). */}
      <div className="overflow-y-auto" style={{ height: "calc(100dvh - 56px)" }}>
        {searchLoading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#e0e0e0] border-t-black rounded-full animate-spin mx-auto" />
          </div>
        ) : searchQuery.length > 0 && searchResults.length > 0 ? (
          <div>
            <div className="px-4 pt-3 pb-1.5 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#999]">
                {locale === "en" ? "Products" : "San pham"}
              </p>
              <p className="text-[11px] text-[#999]">{searchResults.length}{searchResults.length >= 6 ? "+" : ""}</p>
            </div>
            {searchResults.map((result: any) => (
              <Link
                key={result.id}
                to={`/products/${result.handle || result.id}`}
                onClick={() => { onRecordSearch(searchQuery); onClose(); }}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-[#f0f0f0] hover:bg-[#f8f8f8]"
              >
                <div className="w-11 h-14 flex-shrink-0 rounded bg-[#f4f4f4] overflow-hidden">
                  {(result.imageUrl || result.featuredImageUrl) ? (
                    <img src={fixImgUrl(result.imageUrl || result.featuredImageUrl)} alt={displayTitle(result.title, locale, result.titleEn)} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#ccc]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black line-clamp-1">{displayTitle(result.title, locale, result.titleEn)}</p>
                  <p className="text-sm text-[#767676] mt-0.5">{fmtPrice(Number(result.price || result.retailPrice || 0))}</p>
                </div>
              </Link>
            ))}
            <Link
              to={`/search?q=${encodeURIComponent(searchQuery)}`}
              onClick={() => { onRecordSearch(searchQuery); onClose(); }}
              className="flex items-center justify-center gap-1.5 text-xs font-medium text-black py-3.5 hover:bg-[#f8f8f8] transition-colors"
            >
              {locale === "en" ? `View all results for "${searchQuery}"` : `Xem tat ca ket qua cho "${searchQuery}"`}
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
        ) : searchQuery.length > 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-[#767676]">
              {locale === "en" ? `No results for "${searchQuery}"` : `Khong tim thay ket qua cho "${searchQuery}"`}
            </p>
            <p className="text-xs text-[#999] mt-1.5">
              {locale === "en" ? "Try a different search term" : "Thu tu khoa khac"}
            </p>
          </div>
        ) : searchHistory.length > 0 ? (
          /* Empty input + has history → show recent searches as suggestions */
          <div>
            <div className="px-4 pt-3 pb-1.5 flex items-center justify-between border-b border-[#f0f0f0]">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#999]">
                {locale === "en" ? "Recent Searches" : "Tìm kiếm gần đây"}
              </p>
              <button
                type="button"
                onClick={onClearHistory}
                className="text-[11px] text-[#999] hover:text-black transition-colors"
              >
                {locale === "en" ? "Clear" : "Xóa"}
              </button>
            </div>
            {searchHistory.map((entry) => (
              <div key={entry} className="flex items-center border-b border-[#f0f0f0] hover:bg-[#f8f8f8] transition-colors">
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery(entry);
                    onRecordSearch(entry);
                    onClose();
                    window.location.href = `/search?q=${encodeURIComponent(entry)}`;
                  }}
                  className="flex-1 flex items-center gap-3 px-4 py-3 text-left"
                >
                  <svg className="w-4 h-4 text-[#999] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-black truncate">{entry}</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemoveHistoryEntry(entry); }}
                  aria-label={locale === "en" ? `Remove ${entry}` : `Xóa ${entry}`}
                  className="px-4 py-3 text-[#999] hover:text-black transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm text-[#767676]">{t("trySearching", locale as Locale)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Layout ─── //
export default function StorefrontLayout() {
  const {
    recommendations: rawRecommendations,
    currency,
    locale,
    previewAccess,
    retailCustomer,
    market,
    suggestedMarket,
    showMarketSuggestion,
    markets,
    themeColors,
    themeFonts,
    heroSlides,
    announcementBar,
    socialLinks,
    footerText,
    mainMenu,
    footerMenu,
    membership,
    tawktoPropertyId,
    vapidPublicKey,
    effectiveFreeShippingThreshold,
    origin,
    // `localeChosen` was used to server-gate the LocaleSelectModal until
    // 2026-05-11's hydration fix moved that gate client-side. It still
    // ships in the loader payload (harmless) for the (one-day-old)
    // ad-hoc UIs that might use it; remove if no consumer surfaces.
    suggestedLocale,
  } = useLoaderData<typeof loader>();
  const nonce = useNonce();
  // Loader returns either `recommendations: []` (when no shop) or a typed
  // Product[] — TypeScript unions them into a shape where each element is
  // possibly null. Narrow once here so downstream rendering doesn't need
  // scattered non-null assertions.
  const recommendations = ((rawRecommendations as any[]) || []).filter(Boolean) as Array<{
    id: string;
    handle?: string | null;
    title: string;
    titleEn?: string | null;
    featuredImageUrl?: string | null;
    retailPrice?: number | string | null;
  }>;
  const fmtPrice = useCallback((amount: number) => formatStorefrontPrice(amount, currency as string), [currency]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  // Audit 2026-05-17: phone-capture modal trigger. Flips to true the
  // first time the guest reaches 2+ distinct line items in their cart
  // — the highest-intent moment short of checkout itself. Logged-in
  // customers are excluded because we already have their phone on
  // file (RetailCustomer.phone). Modal component itself owns the
  // "already captured / dismissed" persistence so we just supply
  // the intent signal.
  const [phoneCaptureShouldShow, setPhoneCaptureShouldShow] = useState(false);
  // Audit 2026-05-06 (a11y): aria-live announcement string. Updated on
  // add-to-cart so screen-reader users hear feedback. Stays empty
  // outside of cart events; visually hidden via .sr-only.
  const [cartAnnouncement, setCartAnnouncement] = useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileShopExpanded, setMobileShopExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState<CartItem | null>(null);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const confirmationTimer = useRef<ReturnType<typeof setTimeout>>();

  // Wishlist state
  const [wishlist, setWishlist] = useState<string[]>([]);

  // Market popup state
  const [showMarketPopup, setShowMarketPopup] = useState(false);
  const [showCountrySelector, setShowCountrySelector] = useState(false);

  // Tawk.to lazy-load state
  const [tawkLoaded, setTawkLoaded] = useState(false);

  // Lead capture state
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailPopupDismissed, setEmailPopupDismissed] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [footerEmail, setFooterEmail] = useState("");
  const [footerSubmitted, setFooterSubmitted] = useState(false);
  const [footerRewardCode, setFooterRewardCode] = useState<string | null>(null);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search history (localStorage-backed). Hydrated on mount so SSR doesn't
  // leak per-user history into the static HTML. Mirrored into React state so
  // changes (add / clear) re-render the dropdown immediately.
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  useEffect(() => {
    setSearchHistory(loadSearchHistory());
  }, []);

  const recordSearch = useCallback((q: string) => {
    setSearchHistory(saveSearchHistory(q));
  }, []);
  const dropSearch = useCallback((q: string) => {
    setSearchHistory(removeSearchHistoryEntry(q));
  }, []);
  const wipeSearch = useCallback(() => {
    clearSearchHistory();
    setSearchHistory([]);
  }, []);

  // Mobile search overlay state
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Mega menu state
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const megaMenuTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);

  const location = useLocation();
  const navigation = useNavigation();

  // Pre-launch fix 2026-05-11: the `/api/track-pageview` endpoint was
  // wired server-side but no client code was calling it. Result: retail
  // analytics page views = 0 even for days with real traffic. This hook
  // POSTs to that endpoint on every storefront navigation, populating
  // `PageView` rows that feed Page Views / Unique Visitors / Conversion
  // Rate on /admin/retail-analytics + /admin/analytics. Skips admin /
  // portal / rep paths (those aren't even routed through this layout
  // but defensive).
  usePageviewTracker();

  // Load cart from cookie on mount
  useEffect(() => {
    setCartItems(getCartFromCookie());
  }, []);

  // Capture UTM parameters on first visit (for attribution)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get("utm_source");
      const medium = params.get("utm_medium");
      const campaign = params.get("utm_campaign");
      if (source || medium || campaign) {
        const utm = JSON.stringify({
          ...(source && { utm_source: source }),
          ...(medium && { utm_medium: medium }),
          ...(campaign && { utm_campaign: campaign }),
        });
        sessionStorage.setItem("eql_utm", utm);
      }
    } catch {}
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  // Scroll detection for header
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Effective free-shipping threshold (tier-aware: lowered or zero for higher tiers).
  //
  // Bug fix (2026-05-08): the previous fallback `?? FREE_SHIPPING_THRESHOLD`
  // re-introduced the VND-constant-as-USD bug here at the render layer
  // even after the loader's null-pass fix. The announcement bar would
  // render "COMPLIMENTARY SHIPPING ON ORDERS OVER $1,500,000.00" on
  // intl markets — exactly the misrepresentation the loader was trying
  // to avoid. Now: if the loader returns null (non-VN market with no
  // explicit per-market threshold configured), drop the threshold
  // message from the rotation and surface only the other announcements
  // (new arrivals, premium quality, join rewards).
  const freeShippingThreshold = effectiveFreeShippingThreshold;
  const alwaysFreeShipping = freeShippingThreshold === 0;

  const announcements = getAnnouncements(
    locale as Locale,
    // Threshold is always already in the active market currency by the
    // time it gets here — the loader at line ~118 either kept the
    // VND default for VN or used the merchant-configured market value.
    // null = no announceable threshold for this market.
    freeShippingThreshold == null
      ? null
      : formatStorefrontPrice(freeShippingThreshold, currency as string),
    alwaysFreeShipping,
  );
  // Lifetime Set Membership: one rotating line for the program when it's on.
  if (membership?.enabled) {
    const unit = locale === "vi" ? membership.unitLabel.vi : membership.unitLabel.en;
    announcements.push(
      locale === "vi"
        ? `MỖI ${unit.toUpperCase()} = 1 DẤU TRỌN ĐỜI · TRA CỨU THẺ ${membership.programName.toUpperCase()}`
        : `EVERY ${unit.toUpperCase()} = 1 LIFETIME STAMP · FIND YOUR ${membership.programName.toUpperCase()} CARD`,
    );
  }

  // Rotate announcement bar messages
  useEffect(() => {
    const interval = setInterval(() => {
      setAnnouncementIndex((prev) => (prev + 1) % announcements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [announcements.length]);

  // Check announcement bar dismissed state on mount.
  //
  // T3.29: previously a user who dismissed the bar on /about would never
  // see new announcements on any other page. The dismissal now auto-
  // expires after 7 days so merchants can roll new campaigns and reach
  // returning customers without forcing a full cookie wipe. We also
  // migrate the old "1" value to the new timestamp format.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("eql_announcement_dismissed");
      if (!raw) return;
      const dismissedAt = parseInt(raw, 10);
      if (Number.isNaN(dismissedAt) || raw === "1") {
        // Legacy value — treat as dismissed-just-now and let it expire later
        localStorage.setItem("eql_announcement_dismissed", String(Date.now()));
        setAnnouncementDismissed(true);
        return;
      }
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDaysMs) {
        setAnnouncementDismissed(true);
      } else {
        localStorage.removeItem("eql_announcement_dismissed");
      }
    } catch {}
  }, []);

  // Lock body scroll when cart drawer or mobile menu is open
  useEffect(() => {
    if (cartOpen || mobileMenuOpen || mobileSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [cartOpen, mobileMenuOpen, mobileSearchOpen]);

  // Escape key closes cart drawer and mobile menu
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (cartOpen) setCartOpen(false);
        if (mobileMenuOpen) setMobileMenuOpen(false);
        if (mobileSearchOpen) setMobileSearchOpen(false);
        if (searchOpen) setSearchOpen(false);
        if (megaMenuOpen) setMegaMenuOpen(false);
      }
    };
    if (cartOpen || mobileMenuOpen || mobileSearchOpen || searchOpen || megaMenuOpen) {
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }
  }, [cartOpen, mobileMenuOpen, mobileSearchOpen, searchOpen, megaMenuOpen]);

  // Fetch wishlist on mount
  useEffect(() => {
    fetch("/api/wishlist")
      .then((res) => res.json())
      .then((data) => {
        if (data.items) setWishlist(data.items);
      })
      .catch(() => {});
  }, []);

  // Toggle wishlist item
  const toggleWishlist = useCallback(async (productId: string) => {
    const isCurrentlyWishlisted = wishlist.includes(productId);
    // Optimistic update
    setWishlist((prev) =>
      isCurrentlyWishlisted
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
    try {
      await fetch("/api/wishlist", {
        method: "POST",
        body: JSON.stringify({
          productId,
          action: isCurrentlyWishlisted ? "remove" : "add",
        }),
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      // Revert on error
      setWishlist((prev) =>
        isCurrentlyWishlisted
          ? [...prev, productId]
          : prev.filter((id) => id !== productId)
      );
    }
  }, [wishlist]);

  // Market popup: show on first visit if no market cookie set
  useEffect(() => {
    if (typeof document === "undefined") return;
    const hasCookie = document.cookie.includes("eql_market=");
    const dismissed = sessionStorage.getItem("eql_market_popup_dismissed");
    if (!hasCookie && !dismissed && market) {
      const timer = setTimeout(() => setShowMarketPopup(true), 500);
      return () => clearTimeout(timer);
    }
  }, [market]);

  const dismissMarketPopup = useCallback(() => {
    setShowMarketPopup(false);
    setShowCountrySelector(false);
    try { sessionStorage.setItem("eql_market_popup_dismissed", "1"); } catch {}
    // Set market cookie via API
    if (market?.marketId) {
      fetch("/api/set-market", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marketId: market.marketId }),
      }).catch(() => {});
    }
  }, [market]);

  const handleMarketChange = useCallback((selectedMarketId: string) => {
    fetch("/api/set-market", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ marketId: selectedMarketId }),
    }).then(() => {
      window.location.reload();
    }).catch(() => {});
  }, []);

  // Email popup: show after 5s on first visit (check cookie)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const dismissed = document.cookie.includes("eql_popup_dismissed=1");
    if (dismissed) {
      setEmailPopupDismissed(true);
      return;
    }
    const timer = setTimeout(() => {
      setShowEmailPopup(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Inline search: debounced fetch
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(() => {
      fetch(`/api/search-suggestions?q=${encodeURIComponent(searchQuery)}&locale=${locale}`)
        .then((res) => res.json())
        .then((data) => {
          setSearchResults(data.results || data.products || []);
          setSearchLoading(false);
        })
        .catch(() => {
          setSearchResults([]);
          setSearchLoading(false);
        });
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, locale]);

  // Close search dropdown on click outside
  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-search-container]")) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartSubtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Audit 2026-05-17: trigger the phone-capture modal once a guest
  // has reached 2+ distinct cart items. That's the strongest "I'm
  // probably buying" signal short of checkout. Logged-in users are
  // skipped because we already have their phone on file. Modal owns
  // its own "captured / dismissed" persistence so this just supplies
  // the binary intent flag — flipping it false→true is enough.
  useEffect(() => {
    if (retailCustomer) return;
    if (cartItems.length >= 2) setPhoneCaptureShouldShow(true);
  }, [cartItems.length, retailCustomer]);

  // Shipping progress (tier-aware: 0 threshold = always free).
  //
  // Bug fix (2026-05-08): freeShippingThreshold can now be null on
  // non-VN markets without an explicit per-market threshold (the
  // loader stops falling back to the VND constant FREE_SHIPPING_THRESHOLD).
  // When null we can't compute a "you're $X away from free shipping"
  // nudge — treat it as "no progress nudge to render". Callers that
  // condition on `freeShippingThreshold != null` skip the nudge UI;
  // ones that don't get a sensible 0-bar fallback rather than NaN.
  const shippingProgress = freeShippingThreshold == null
    ? 0
    : freeShippingThreshold === 0
      ? 100
      : Math.min((cartSubtotal / freeShippingThreshold) * 100, 100);
  const shippingRemaining = freeShippingThreshold == null
    ? 0
    : Math.max(freeShippingThreshold - cartSubtotal, 0);
  const hasFreeShipping = freeShippingThreshold != null &&
    cartSubtotal >= freeShippingThreshold;

  const addToCart = useCallback((item: CartItem) => {
    setCartAnnouncement(
      locale === "vi"
        ? `Đã thêm ${item.productTitle || "sản phẩm"} vào giỏ hàng`
        : `Added ${item.productTitle || "item"} to your cart`,
    );
    setCartItems((prev) => {
      const existing = prev.find((i) => i.variantId === item.variantId);
      // Inventory cap: prefer the most-recent maxQuantity (callers may
      // pass a fresher number than the one stored on an existing cart
      // line). When neither is set, treat as uncapped and fall back to
      // the server-side stock check at checkout.
      const cap = item.maxQuantity ?? existing?.maxQuantity ?? Infinity;
      let next: CartItem[];
      if (existing) {
        const newQty = Math.min(existing.quantity + item.quantity, cap);
        next = prev.map((i) =>
          i.variantId === item.variantId
            ? { ...i, quantity: newQty, maxQuantity: cap === Infinity ? i.maxQuantity : cap }
            : i,
        );
      } else {
        next = [...prev, { ...item, quantity: Math.min(item.quantity, cap) }];
      }
      saveCartToCookie(next);
      return next;
    });
    setCartOpen(true);

    // ─── Meta Pixel + CAPI: AddToCart (centralized for all ATC sources) ─── //
    try {
      const atcEventId = crypto.randomUUID();
      const atcData = {
        content_ids: [item.productId || item.variantId],
        content_type: "product_group",
        content_name: item.productTitle,
        value: item.price,
        currency: item.currency || currency || "VND",
      };
      // Client-side pixel
      if (typeof window !== "undefined" && typeof (window as any).fbq === "function") {
        (window as any).fbq("track", "AddToCart", atcData, { eventID: atcEventId });
      }
      // Server-side CAPI via API route (fire-and-forget)
      if (typeof window !== "undefined") {
        fetch("/api/fb-capi", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_name: "AddToCart",
            event_id: atcEventId,
            source_url: window.location.href,
            custom_data: atcData,
          }),
          keepalive: true,
        }).catch(() => {});

        // First-party telemetry (2026-05-12): mirrors the CAPI write
        // into our own DB so admin/retail-analytics has a funnel source
        // that doesn't depend on FB Pixel or Clarity cookie consent.
        fetch("/api/retail-events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_name: "add_to_cart",
            product_id: item.productId,
            variant_id: item.variantId,
            price: item.price,
            value: item.price * (item.quantity || 1),
            currency: item.currency || currency || "VND",
            quantity: item.quantity || 1,
            session_id: getOrCreateRetailSessionId(),
            page_url: window.location.pathname + window.location.search,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // Non-critical — never break add-to-cart
    }
  }, [currency]);

  const updateQuantity = useCallback((variantId: string, quantity: number) => {
    setCartItems((prev) => {
      const target = prev.find((i) => i.variantId === variantId);
      // Inventory cap from the cart row itself (set at add-to-cart time).
      // Undefined cap = uncapped; server-side stock check at order
      // creation is the safety net.
      const cap = target?.maxQuantity ?? Infinity;
      const cappedQty = Math.min(quantity, cap);
      const next = cappedQty <= 0
        ? prev.filter((i) => i.variantId !== variantId)
        : prev.map((i) => (i.variantId === variantId ? { ...i, quantity: cappedQty } : i));
      saveCartToCookie(next);
      return next;
    });
  }, []);

  const removeFromCart = useCallback((variantId: string) => {
    setCartItems((prev) => {
      const next = prev.filter((i) => i.variantId !== variantId);
      saveCartToCookie(next);
      return next;
    });
  }, []);

  const closeCart = useCallback(() => {
    setCartOpen(false);
  }, []);

  // Newsletter submission handler (supports source param)
  const handleNewsletterSubmit = useCallback(async (email: string, source: string): Promise<string | null> => {
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), source }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => null);
      // First-order incentive code (WELCOME10) — surfaced in the success
      // state of whichever capture UI called us.
      return (data && typeof data.code === "string" && data.code) || null;
    } catch {
      // Silently fail
      return null;
    }
  }, []);

  // Close email popup and set cookie so it doesn't show again
  const dismissEmailPopup = useCallback(() => {
    setShowEmailPopup(false);
    setEmailPopupDismissed(true);
    document.cookie = "eql_popup_dismissed=1;path=/;max-age=" + 60 * 60 * 24 * 30 + ";SameSite=Lax";
  }, []);

  // Dismiss announcement bar — T3.29: store timestamp so it auto-expires after 7 days
  const dismissAnnouncement = useCallback(() => {
    setAnnouncementDismissed(true);
    try { localStorage.setItem("eql_announcement_dismissed", String(Date.now())); } catch {}
  }, []);

  // Mega menu handlers
  const openMegaMenu = useCallback(() => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    setMegaMenuOpen(true);
  }, []);

  const closeMegaMenu = useCallback(() => {
    megaMenuTimeoutRef.current = setTimeout(() => setMegaMenuOpen(false), 200);
  }, []);

  // Language switcher — set cookie and reload (no /en/ URL prefix needed)
  const handleLanguageSwitch = useCallback((newLocale: string) => {
    const maxAge = 365 * 24 * 60 * 60;
    document.cookie = `eql_locale=${newLocale};path=/;max-age=${maxAge};SameSite=Lax;Secure`;
    // Also mark the locale as explicitly chosen so the first-visit
    // LocaleSelectModal doesn't appear on a subsequent visit (added
    // 2026-05-11). Without this, an existing customer who switched
    // language via the header BEFORE the modal feature shipped would
    // see the picker once when they returned. Setting the gate flag
    // here treats any in-header switch as an explicit choice.
    document.cookie = `eql_locale_chosen=true;path=/;max-age=${maxAge};SameSite=Lax;Secure`;
    // Strip /en/ prefix if present (legacy URLs), then reload
    const currentPath = location.pathname.replace(/^\/en\/?/, "/") || "/";
    window.location.href = `${currentPath}${location.search}`;
  }, [location.pathname, location.search]);

  const isHomepage = location.pathname === "/" || location.pathname === "/en" || location.pathname === "/en/";
  // On homepage and not scrolled = transparent header with white text
  const isTransparent = isHomepage && !scrolled && !mobileMenuOpen;

  // Build desktop nav links from mainMenu or fallback
  //
  // Defensive normalisation: REMIX-4 in Sentry (2026-05-11) recorded a
  // minified `Vt[r].children` TypeError on /collections — we couldn't
  // pinpoint the exact downstream access without source maps. To close
  // the issue source-side: `.filter(Boolean)` drops any null entries
  // in `items` before the .map runs, and `children` is coerced to a
  // real array (`?? []` plus `Array.isArray` guard) so consumers never
  // see a non-array value even if cache state is somehow malformed.
  const desktopNavLinks = mainMenu?.items?.length
    ? (mainMenu.items as any[]).filter(Boolean).map((item: any) => {
        const children = Array.isArray(item.children) ? item.children : [];
        return {
          label: locale === "en" && item.titleEn ? item.titleEn : item.title,
          href: item.url || item.href || "/products",
          hasChildren: children.length > 0,
          children,
          isShop: (item.title || "").toLowerCase().includes("shop") || (item.url || "").includes("/products"),
        };
      })
    : [
        { label: t("shop", locale as Locale), href: "/products", hasChildren: false, children: [], isShop: true },
        // 2026-05-12: per merchant ask, replaced the "Bộ sưu tập" →
        // /collections nav item with "All Products" → /products. Restore
        // the Collections link from git history if we want it back.
        { label: t("allProducts", locale as Locale), href: "/products", hasChildren: false, children: [], isShop: false },
      ];

  // Build footer sections from footerMenu or fallback. Same defensive
  // normalisation as desktopNavLinks above — never let a non-array
  // children value reach the .map call.
  const footerSections = footerMenu?.items?.length
    ? (footerMenu.items as any[]).filter(Boolean).map((item: any) => {
        const children = Array.isArray(item.children) ? item.children : [];
        return {
          title: locale === "en" && item.titleEn ? item.titleEn : item.title,
          links: children.filter(Boolean).map((child: any) => ({
            label: locale === "en" && child.titleEn ? child.titleEn : child.title,
            href: child.url || child.href || "#",
          })),
        };
      })
    : null;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Organization JSON-LD on every storefront page so search engines +
          rich-result tools can attach brand identity to any indexed URL.
          Lives in the layout body (not a meta export) because Remix v2
          child-route meta REPLACES parent meta. */}
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Diolo",
            url: origin,
            logo: `${origin}/diolo-logo.png`,
            // sameAs only when the merchant has configured real social
            // profiles (admin → Shop.socialLinks); fabricated handles in
            // structured data are an SEO liability.
            ...((socialLinks as Record<string, string> | null) &&
            Object.values(socialLinks as Record<string, string>).some(Boolean)
              ? {
                  sameAs: Object.values(
                    socialLinks as Record<string, string>,
                  ).filter(Boolean),
                }
              : {}),
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              url: `${origin}/contact`,
            },
          }),
        }}
      />
      {/* ─── Dismissible Rotating Announcement Bar ───
          CLS fix (2026-05-11): the bar previously rendered conditionally
          on `!announcementDismissed && !isHomepage`. Returning users on
          non-homepage routes had `announcementDismissed=false` at SSR
          (initial state) but their useEffect would read localStorage and
          flip to `true`, collapsing 30px and causing a Cumulative Layout
          Shift (Clarity reported CLS=0.23, "needs improvement"). Now the
          OUTER wrapper always reserves 30px on non-homepage so dismissal
          stays visually in-place; only the inner content disappears. */}
      {!isHomepage && (
        <div
          className="h-[30px]"
          aria-hidden={announcementDismissed || undefined}
          data-announcement-wrapper="true"
        >
          {!announcementDismissed && (
        <div className="sf-announcement relative overflow-hidden h-[30px] flex items-center justify-center" data-announcement="true">
          {announcements.map((msg, i) => (
            <span
              key={i}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
                i === announcementIndex
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-3"
              }`}
            >
              {msg}
            </span>
          ))}
          {/* Close button */}
          <button
            onClick={dismissAnnouncement}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10"
            aria-label="Dismiss announcement"
          >
            <span className="text-xs leading-none">✕</span>
          </button>
        </div>
          )}
        </div>
      )}

      {/* ─── Header ─── */}
      <header
        className={`sf-header ${isHomepage ? "absolute left-0 right-0" : ""}`}
        data-scrolled={scrolled || !isHomepage ? "true" : undefined}
        data-announcement="true"
        style={isHomepage ? { position: scrolled ? "fixed" : "absolute" } : undefined}
      >
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <div className={`flex h-16 lg:h-20 items-center justify-between ${!isTransparent ? "sf-header-bar" : ""}`}>
            {/* Left: Mobile hamburger + Desktop Nav */}
            <div className="flex items-center gap-6">
              <button
                className={`sf-icon-btn lg:hidden transition-colors ${isTransparent ? "text-white" : "text-black"}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-drawer"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12h18M3 6h18M3 18h18" />
                  </svg>
                )}
              </button>

              {/* Desktop Navigation — dynamic from mainMenu */}
              <nav className="hidden lg:flex items-center gap-8">
                {desktopNavLinks.map((navItem, idx) => (
                  <div
                    key={idx}
                    className="relative"
                    onMouseEnter={navItem.isShop ? openMegaMenu : undefined}
                    onMouseLeave={navItem.isShop ? closeMegaMenu : undefined}
                    ref={navItem.isShop ? megaMenuRef : undefined}
                  >
                    <Link
                      to={localePath(navItem.href, locale as Locale)}
                      className={`sf-nav-link text-[13px] font-normal tracking-[0.01em] transition-colors ${
                        isTransparent
                          ? "text-white/90 hover:text-white"
                          : "text-black hover:opacity-70"
                      }`}
                    >
                      {navItem.label}
                    </Link>

                    {/* Mega Menu — on the "Shop" nav item */}
                    {navItem.isShop && megaMenuOpen && (
                      <div
                        className="df-megamenu absolute top-full left-0 min-w-[760px] bg-white border border-[#e5e5e5] p-6 z-[60]"
                        onMouseEnter={openMegaMenu}
                        onMouseLeave={closeMegaMenu}
                      >
                        <div className="grid grid-cols-[1fr_1fr_1fr_1.4fr] gap-6">
                          {/* Column 1: By Activity */}
                          <div>
                            <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#767676] mb-3">
                              {locale === "en" ? "By Activity" : "Theo hoạt động"}
                            </h4>
                            <ul className="space-y-1.5">
                              {MEGA_MENU_ACTIVITIES.map((act) => (
                                <li key={act.key}>
                                  <Link
                                    to={localePath(act.href, locale as Locale)}
                                    className="text-[13px] text-black hover:opacity-70 transition-colors"
                                    onClick={() => setMegaMenuOpen(false)}
                                  >
                                    {locale === "en" ? act.en : act.vi}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Column 2: By Type */}
                          <div>
                            <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#767676] mb-3">
                              {locale === "en" ? "By Type" : "Theo loại"}
                            </h4>
                            <ul className="space-y-1.5">
                              {MEGA_MENU_TYPES.map((typ) => (
                                <li key={typ.key}>
                                  <Link
                                    to={localePath(typ.href, locale as Locale)}
                                    className="text-[13px] text-black hover:opacity-70 transition-colors"
                                    onClick={() => setMegaMenuOpen(false)}
                                  >
                                    {locale === "en" ? typ.en : typ.vi}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Column 3: Quick Links */}
                          <div>
                            <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#767676] mb-3">
                              {locale === "en" ? "Quick Links" : "Liên kết nhanh"}
                            </h4>
                            <ul className="space-y-1.5">
                              {MEGA_MENU_QUICK_LINKS(locale as string).map((link) => (
                                <li key={link.href}>
                                  <Link
                                    to={localePath(link.href, locale as Locale)}
                                    className="text-[13px] text-black hover:opacity-70 transition-colors"
                                    onClick={() => setMegaMenuOpen(false)}
                                  >
                                    {link.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Column 4: Featured editorial card */}
                          <Link
                            to={localePath("/products?tag=new", locale as Locale)}
                            onClick={() => setMegaMenuOpen(false)}
                            className="group relative block overflow-hidden bg-[#f4f4f4]"
                          >
                            <img
                              src="/editorial/night-net.jpg"
                              alt={locale === "en" ? "New Arrivals" : "Hàng mới về"}
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-4">
                              <p className="text-[10px] uppercase tracking-[0.18em] text-white/80">
                                {locale === "en" ? "Just Dropped" : "Vừa ra mắt"}
                              </p>
                              <p className="text-[15px] font-medium tracking-[-0.02em] text-white">
                                {locale === "en" ? "New Arrivals" : "Hàng mới về"}
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Center: DIOLO wordmark (text until the brand logo assets land
                — swap to an <img> in STEP 6 without layout changes) */}
            <Link
              to={localePath("/", locale as Locale)}
              className="absolute left-1/2 -translate-x-1/2"
              aria-label="Diolo home"
            >
              <span
                className={`font-brand text-[20px] lg:text-[22px] font-bold uppercase tracking-normal leading-none transition-colors ${
                  isTransparent ? "text-white" : "text-black"
                }`}
              >
                DIOLO
              </span>
            </Link>

            {/* Right: Desktop search, Language switcher, Icons */}
            <div className="flex items-center gap-1 sm:gap-4">
              {/* Inline Search (Desktop) */}
              <div data-search-container>
                <InlineSearchDesktop
                  locale={locale as string}
                  isTransparent={isTransparent}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  searchResults={searchResults}
                  searchLoading={searchLoading}
                  searchOpen={searchOpen}
                  setSearchOpen={setSearchOpen}
                  inputRef={searchInputRef}
                  fmtPrice={fmtPrice}
                  searchHistory={searchHistory}
                  onRecordSearch={recordSearch}
                  onRemoveHistoryEntry={dropSearch}
                  onClearHistory={wipeSearch}
                  onClose={() => {
                    setSearchOpen(false);
                    setSearchQuery("");
                  }}
                />
              </div>

              {/* Language Switcher (Desktop) */}
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleLanguageSwitch(locale === "en" ? "vi" : "en"); }}
                className={`hidden lg:block text-[11px] font-normal tracking-[0.05em] transition-colors ${
                  isTransparent ? "text-white/60 hover:text-white" : "text-[#767676] hover:opacity-70"
                }`}
              >
                {locale === "en" ? "VI" : "EN"}
              </a>

              {/* About (desktop only) */}
              <Link
                to={localePath("/about", locale as Locale)}
                className={`hidden lg:block sf-nav-link text-[13px] font-normal tracking-[0.01em] transition-colors ${
                  isTransparent
                    ? "text-white/90 hover:text-white"
                    : "text-black hover:opacity-70"
                }`}
              >
                {t("about", locale as Locale)}
              </Link>

              {/* Search icon (mobile) — T4.11: sf-icon-btn enforces 44×44 tap target on mobile */}
              <button
                onClick={() => setMobileSearchOpen(true)}
                className={`sf-icon-btn lg:hidden transition-colors ${
                  isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-[#767676] hover:opacity-70"
                }`}
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </button>

              {/* Wishlist — T4.11: 44×44 mobile tap target */}
              <Link
                to={localePath("/wishlist", locale as Locale)}
                className={`sf-icon-btn relative transition-colors ${
                  isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-[#767676] hover:opacity-70"
                }`}
                aria-label="Wishlist"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
                {wishlist.length > 0 && (
                  <span className={`absolute -top-1 -right-1 text-[8px] font-medium w-3.5 h-3.5 flex items-center justify-center rounded-full ${
                    isTransparent ? "bg-white text-black" : "bg-black text-white"
                  }`}>
                    {wishlist.length > 9 ? "9+" : wishlist.length}
                  </span>
                )}
              </Link>

              {/* Account — T4.11: 44×44 mobile tap target */}
              <Link
                to={localePath("/account", locale as Locale)}
                className={`sf-icon-btn hidden sm:inline-flex transition-colors ${
                  isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-[#767676] hover:opacity-70"
                }`}
                aria-label="Account"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </Link>

              {/* Cart — T2.16: aria-label includes item count; T4.11: 44×44 tap target */}
              <button
                onClick={() => setCartOpen(true)}
                className={`sf-icon-btn relative transition-colors ${
                  isTransparent
                    ? "text-white/80 hover:text-white"
                    : "text-[#767676] hover:opacity-70"
                }`}
                aria-label={
                  cartCount === 0
                    ? (locale === "vi" ? "Giỏ hàng trống" : "Cart, empty")
                    : locale === "vi"
                      ? `Giỏ hàng với ${cartCount} sản phẩm`
                      : `Cart with ${cartCount} ${cartCount === 1 ? "item" : "items"}`
                }
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                {cartCount > 0 && (
                  <span className={`absolute -top-1 -right-1 text-[8px] font-medium w-3.5 h-3.5 flex items-center justify-center rounded-full ${
                    isTransparent ? "bg-white text-black" : "bg-black text-white"
                  }`}>
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ─── Mobile Drawer (slide-in panel) ─── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/25 z-[80] transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />
      {/* Drawer panel */}
      <div
        className="fixed inset-y-0 left-0 w-full max-w-none sm:w-[400px] sm:max-w-[400px] bg-white z-[90] transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col lg:hidden"
        style={{ transform: mobileMenuOpen ? "translateX(0)" : "translateX(-100%)" }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 h-14 border-b border-[#e0e0e0]">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="font-brand text-[17px] font-bold uppercase tracking-normal leading-none text-black">
            DIOLO
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-black hover:opacity-70"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Drawer body */}
        <nav className="flex-1 overflow-y-auto px-5 py-4">
          {/* Dynamic menu items or fallback */}
          {desktopNavLinks.map((navItem, idx) => (
            <div key={idx}>
              {navItem.isShop ? (
                <>
                  <button
                    onClick={() => setMobileShopExpanded(!mobileShopExpanded)}
                    className="flex items-center justify-between w-full py-3 text-xs font-normal uppercase tracking-[0.1em] text-black"
                  >
                    <span>{navItem.label}</span>
                    <svg
                      className={`w-4 h-4 text-black transition-transform ${mobileShopExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileShopExpanded && (
                    <div className="pl-3 pb-3 space-y-1">
                      <Link to={localePath("/products", locale as Locale)} onClick={() => setMobileMenuOpen(false)} className="block py-2 text-xs font-normal text-black hover:opacity-70">
                        {t("shopAll", locale as Locale)}
                      </Link>
                      <div className="pt-3 pb-1">
                        <p className="text-[10px] font-normal uppercase tracking-[0.15em] text-[#767676] mb-1.5">
                          {locale === "en" ? "By Activity" : "Theo hoạt động"}
                        </p>
                        {MEGA_MENU_ACTIVITIES.map((act) => (
                          <Link
                            key={act.key}
                            to={localePath(act.href, locale as Locale)}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block py-1.5 text-xs font-normal text-black hover:opacity-70"
                          >
                            {locale === "en" ? act.en : act.vi}
                          </Link>
                        ))}
                      </div>
                      <div className="pt-3 pb-1">
                        <p className="text-[10px] font-normal uppercase tracking-[0.15em] text-[#767676] mb-1.5">
                          {locale === "en" ? "By Type" : "Theo loại"}
                        </p>
                        {MEGA_MENU_TYPES.map((typ) => (
                          <Link
                            key={typ.key}
                            to={localePath(typ.href, locale as Locale)}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block py-1.5 text-xs font-normal text-black hover:opacity-70"
                          >
                            {locale === "en" ? typ.en : typ.vi}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={localePath(navItem.href, locale as Locale)}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 text-xs font-normal uppercase tracking-[0.1em] text-black"
                >
                  {navItem.label}
                </Link>
              )}
            </div>
          ))}

          {/* Additional mobile links */}
          <div className="border-t border-[#e0e0e0] mt-3 pt-3 space-y-1">
            <Link to={localePath("/about", locale as Locale)} onClick={() => setMobileMenuOpen(false)} className="block py-3 text-xs font-normal uppercase tracking-[0.1em] text-black">
              {t("about", locale as Locale)}
            </Link>
            <Link to={localePath("/wishlist", locale as Locale)} onClick={() => setMobileMenuOpen(false)} className="block py-3 text-xs font-normal uppercase tracking-[0.1em] text-black">
              {t("wishlist", locale as Locale)} {wishlist.length > 0 && `(${wishlist.length})`}
            </Link>
            <Link to={localePath("/account", locale as Locale)} onClick={() => setMobileMenuOpen(false)} className="block py-3 text-xs font-normal uppercase tracking-[0.1em] text-black">
              {t("account", locale as Locale)}
            </Link>
          </div>

          {/* Mobile language switcher */}
          <div className="border-t border-[#e0e0e0] mt-3 pt-4">
            <p className="text-[10px] font-normal uppercase tracking-[0.15em] text-[#767676] mb-2">
              {locale === "en" ? "Language" : "Ngon ngu"}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setMobileMenuOpen(false); handleLanguageSwitch("vi"); }}
                className={`text-xs font-normal px-3 py-2 border transition-colors rounded-none ${
                  locale === "vi" ? "border-black bg-black text-white" : "border-[#e0e0e0] text-black hover:border-black"
                }`}
              >
                Tiếng Việt
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); handleLanguageSwitch("en"); }}
                className={`text-xs font-normal px-3 py-2 border transition-colors rounded-none ${
                  locale === "en" ? "border-black bg-black text-white" : "border-[#e0e0e0] text-black hover:border-black"
                }`}
              >
                English
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* ─── Mobile Search Overlay ─── */}
      {mobileSearchOpen && (
        <MobileSearchOverlay
          locale={locale as string}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          searchLoading={searchLoading}
          fmtPrice={fmtPrice}
          searchHistory={searchHistory}
          onRecordSearch={recordSearch}
          onRemoveHistoryEntry={dropSearch}
          onClearHistory={wipeSearch}
          onClose={() => {
            setMobileSearchOpen(false);
            setSearchQuery("");
            setSearchResults([]);
          }}
        />
      )}

      {/* ─── Loading bar ─── */}
      {navigation.state === "loading" && (
        <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-black/20">
          <div className="h-full bg-black animate-pulse" style={{ width: "70%" }} />
        </div>
      )}

      {/* Cart confirmation toast removed — cart drawer opens directly */}

      {/* Audit 2026-05-06 (a11y): aria-live region announces cart
          additions to screen readers. Visually hidden via sr-only;
          assistive tech polls the region and reads the new content
          when state changes. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {cartAnnouncement}
      </div>

      {/* Audit 2026-05-06 (compliance, item #12): cookie consent
          banner. Renders only when no eql_cookie_consent cookie is
          set yet — once user accepts/rejects, the banner self-hides
          and a page reload picks up the new analytics gate state
          set by app/root.tsx loader. */}
      <CookieConsent locale={locale} />

      {/* 2026-05-11: first-visit language picker.
          Replaces the previous silent server-side auto-redirect to en
          — customer feedback after cutover was that an explicit choice
          reads better than a heuristic guess. The picker pre-highlights
          `suggestedLocale` (market language → suggested-market language
          → Accept-Language q-weights → vi default), so a typical
          visitor confirms with a single click.

          The modal self-gates client-side on the `eql_locale_chosen`
          cookie (same pattern as <CookieConsent>) — server-side gating
          was removed in the v461 hydration fix because /collections
          (s-maxage=600) and PDPs (s-maxage=300) public-cache their
          HTML; a cached SSR with the modal could be served to a user
          whose cookie says they already chose, causing React #418
          hydration mismatches. Now SSR always renders `null` and the
          modal pops in client-side if needed — one frame of flash but
          cache-safe across users. */}
      <LocaleSelectModal suggestedLocale={suggestedLocale as Locale} />

      {/* Phone-capture lead modal — REMOVED 2026-05-20 per merchant
          direction. The "Get 10% Off Your First Order" offer is being
          replaced by a forthcoming loyalty-program signup. The modal
          component (PhoneCaptureModal.tsx) is kept on disk in case a
          variant is needed later, but no longer mounted here.
          The phoneCaptureShouldShow useState + useEffect remain unused
          and can be removed in a follow-up cleanup PR. */}
      {/* <PhoneCaptureModal locale={locale} shouldShow={phoneCaptureShouldShow} /> */}

      {/* ─── Main Content ─── */}
      {/* T3.28: skip-to-content link — visible only when focused, so keyboard
          users can bypass the header nav and jump straight to the main area. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-black focus:text-white focus:px-4 focus:py-2 focus:text-xs focus:uppercase focus:tracking-widest"
      >
        {locale === "vi" ? "Bỏ qua đến nội dung chính" : "Skip to main content"}
      </a>
      {/*
        overflow-x-CLIP (not -hidden) — same gotcha as the April-7 fix on
        html/body. `overflow-x: hidden` forces the browser to promote
        overflow-y to `auto` (CSS spec: can't have one axis hidden + the
        other visible), which silently turns <main> into a scroll container
        and breaks every position:sticky descendant (the PDP thumbnail strip
        + right info column would lose their pinning behaviour). `clip`
        prevents horizontal scrollbars without establishing a scroll
        container, so sticky still works.
      */}
      <main id="main-content" className="flex-1 overflow-x-clip">
        <Outlet context={{ addToCart, updateQuantity, removeFromCart, cartItems, cartCount, wishlist, toggleWishlist, cartOpen, locale, currency, membership, heroSlides, announcementBar, socialLinks, fmtPrice, retailCustomer, market, effectiveFreeShippingThreshold }} />
      </main>

      {/* ─── Footer ─── */}
      {/* Diolo rebrand 2026-06-10: white minimal footer — loyalty/newsletter
          block on the left, tight link columns, country/locale column, and
          a single legal bottom row. Payment-icon + trust-badge rows removed
          (recoverable from git history). */}
      <footer className="bg-white border-t border-[#e5e5e5] mt-4">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-12 lg:gap-8 mb-12">
            {/* Loyalty / newsletter block */}
            <div className="col-span-2 lg:col-span-4 lg:pr-10">
              <h4 className="text-[12px] font-medium text-black mb-4">
                {locale === "vi" ? "Nhận tin từ Diolo" : "Join the list"}
              </h4>
              <p className="text-[13px] leading-relaxed text-[#757575] mb-5 max-w-xs">
                {locale === "vi"
                  ? "Drop mới, Court Session và quyền truy cập sớm — danh sách luôn biết trước."
                  : "New drops, Court Sessions and early access — the list hears first."}
              </p>
              {footerSubmitted ? (
                <div>
                  <p className="text-[13px] text-black">{t("thankYouSubscribing", locale as Locale)}</p>
                </div>
              ) : (
                <form
                  className="flex items-end gap-3 max-w-sm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (footerEmail.trim()) {
                      void handleNewsletterSubmit(footerEmail, "footer").then((code) =>
                        setFooterRewardCode(code),
                      );
                      setFooterSubmitted(true);
                    }
                  }}
                >
                  <input
                    type="email"
                    placeholder={t("emailPlaceholder", locale as Locale)}
                    value={footerEmail}
                    onChange={(e) => setFooterEmail(e.target.value)}
                    required
                    className="flex-1 min-w-0 border-0 border-b border-[#d5d5d5] bg-transparent px-0 py-2 text-[16px] md:text-[13px] placeholder:text-[#999] focus:border-black focus:outline-none focus:ring-0"
                  />
                  <button type="submit" className="df-arrow-link shrink-0 text-black">
                    <span aria-hidden="true">→</span> {locale === "vi" ? "ĐĂNG KÝ" : "SIGN UP"}
                  </button>
                </form>
              )}
            </div>

          {/* Link columns — dynamic footerMenu when configured, else the
              standard Help/Company pair. Social + Country columns always
              render. */}
            {(footerSections && footerSections.length > 0
              ? footerSections.slice(0, 2).map((section) => ({
                  title: section.title,
                  links: section.links as { label: string; href: string }[],
                }))
              : [
                  {
                    title: locale === "vi" ? "Hỗ trợ khách hàng" : "Client Services",
                    links: [
                      { label: t("faqs", locale as Locale), href: "/faq" },
                      { label: locale === "en" ? "Track Order" : "Tra cứu đơn hàng", href: "/order-tracking" },
                      { label: t("shipping", locale as Locale), href: "/shipping" },
                      { label: t("returns", locale as Locale), href: "/returns" },
                      { label: locale === "en" ? "Size Guide" : "Hướng dẫn chọn size", href: "/size-guide" },
                    ],
                  },
                  {
                    title: t("company", locale as Locale),
                    links: [
                      { label: t("about", locale as Locale), href: "/about" },
                      { label: locale === "en" ? "Lookbook" : "Bộ sưu tập ảnh", href: "/lookbook" },
                      { label: t("wholesale", locale as Locale), href: "/portal" },
                      { label: t("contact", locale as Locale), href: "/contact" },
                    ],
                  },
                ]
            ).map((section, idx) => (
              <div key={idx} className="lg:col-span-2">
                <h4 className="text-[12px] font-medium text-black mb-4">{section.title}</h4>
                <ul className="space-y-2.5">
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      <Link
                        to={localePath(link.href, locale as Locale)}
                        className="text-[11px] text-[#757575] hover:text-black transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Social column — driven by Shop.socialLinks (admin-configured).
                Hidden until the merchant adds Diolo's real profiles; we never
                fall back to hardcoded handles (the fork shipped with EQL's).
                Known networks render as an icon, the rest as a text link. */}
            {(() => {
              const links = Object.entries(
                (socialLinks as Record<string, string> | null) ?? {},
              ).filter(([, url]) => !!url);
              if (links.length === 0) return null;
              const labelFor = (key: string) =>
                key.charAt(0).toUpperCase() + key.slice(1).replace(/tok$/i, "Tok");
              return (
                <div className="lg:col-span-2">
                  <h4 className="text-[12px] font-medium text-black mb-4">Social</h4>
                  <ul className="flex flex-wrap items-center gap-4">
                    {links.map(([key, url]) => (
                      <li key={key}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={labelFor(key)}
                          title={labelFor(key)}
                          className="inline-flex items-center text-[11px] text-[#757575] hover:text-black transition-colors"
                        >
                          {hasSocialIcon(key) ? (
                            <SocialIcon name={key} className="h-[18px] w-[18px]" />
                          ) : (
                            labelFor(key)
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Country / language column */}
            <div className="lg:col-span-2">
              <h4 className="text-[12px] font-medium text-black mb-4">
                {locale === "vi" ? "Quốc gia" : "Country"}
              </h4>
              <p className="text-[11px] text-[#757575] mb-3">
                {locale === "vi" ? "Giao đến" : "Shipping to"}:{" "}
                <span className="text-black">
                  {market?.marketName || (locale === "vi" ? "Quốc tế" : "International")}
                </span>
                {market?.currency ? <span className="text-[#999]"> ({market.currency})</span> : null}
              </p>
              <div className="flex items-center gap-1.5 text-[11px] text-[#757575]">
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch("vi")}
                  className={`${locale === "vi" ? "text-black underline underline-offset-2" : "hover:text-black"} transition-colors`}
                  aria-current={locale === "vi" ? "true" : undefined}
                >
                  Tiếng Việt
                </button>
                <span className="text-[#d0d0d0]">/</span>
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch("en")}
                  className={`${locale === "en" ? "text-black underline underline-offset-2" : "hover:text-black"} transition-colors`}
                  aria-current={locale === "en" ? "true" : undefined}
                >
                  English
                </button>
              </div>
            </div>
          </div>

          {/* Region + language now live in the Country column above. */}

          {/* Optional merchant footer text */}
          {footerText && (
            <p className="text-[11px] text-[#757575] mb-6">{footerText}</p>
          )}

          {/* Legal row */}
          {/* suppressHydrationWarning: `new Date().getFullYear()` is read at
              SSR AND on hydration — a cached SSR shell served across a year
              boundary would otherwise log a hydration error. */}
          <div className="border-t border-[#e5e5e5] pt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
            <p className="text-[11px] text-black" suppressHydrationWarning>
              &copy; {new Date().getFullYear()} DIOLO
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[#757575]">
              <Link to={localePath("/terms", locale as Locale)} className="hover:text-black transition-colors">{t("terms", locale as Locale)}</Link>
              <Link to={localePath("/privacy", locale as Locale)} className="hover:text-black transition-colors">{t("privacy", locale as Locale)}</Link>
              <Link to={localePath("/returns", locale as Locale)} className="hover:text-black transition-colors">
                {(locale as Locale) === "vi" ? "Chính sách đổi trả" : "Shipping & Returns Policy"}
              </Link>
              <Link to={localePath("/accessibility", locale as Locale)} className="hover:text-black transition-colors">
                {(locale as Locale) === "vi" ? "Khả năng tiếp cận" : "Accessibility"}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ─── Tawk.to Live Chat (lazy-loaded on click) ─── */}
      {tawktoPropertyId && !tawkLoaded && (
        <button
          onClick={() => {
            setTawkLoaded(true);
            const w = window as any;
            w.Tawk_API = w.Tawk_API || {};
            w.Tawk_LoadStart = new Date();
            const s = document.createElement("script");
            s.async = true;
            s.src = `https://embed.tawk.to/${tawktoPropertyId}/default`;
            s.charset = "UTF-8";
            s.setAttribute("crossorigin", "*");
            document.head.appendChild(s);
            // Auto-open chat once loaded
            w.Tawk_API.onLoad = () => { w.Tawk_API.maximize(); };
          }}
          className="fixed bottom-5 right-5 z-[100] w-14 h-14 bg-black rounded-full flex items-center justify-center shadow-lg hover:bg-black/80 transition-colors"
          aria-label="Open live chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6">
            <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
            <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
          </svg>
        </button>
      )}

      {/* ─── Market/Country Popup ─── */}
      {showMarketPopup && market && !showCountrySelector && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]" onClick={dismissMarketPopup} />
          <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
            <div className="relative bg-white max-w-lg w-full shadow-2xl p-8 sm:p-10">
              <div className="flex items-start justify-between mb-6">
                <h2 className="font-display text-2xl font-light tracking-normal">
                  {locale === "vi" ? "Bạn đang mua sắm tại " : "You're Shopping in "}
                  {market.marketName}.
                </h2>
                <button onClick={dismissMarketPopup} className="text-sm text-gray-500 hover:opacity-70 ml-4 whitespace-nowrap">
                  {locale === "vi" ? "Đóng" : "Close"}
                </button>
              </div>
              <ul className="space-y-2 mb-8 text-sm text-gray-700">
                {market.freeShippingThreshold && (
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">—</span>
                    <span>
                      {locale === "vi"
                        ? `Miễn phí vận chuyển cho đơn hàng trên ${fmtPrice(market.freeShippingThreshold)}`
                        : `Free shipping for all orders above ${fmtPrice(market.freeShippingThreshold)}`}
                    </span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">—</span>
                  <span>
                    {locale === "vi"
                      ? "Thuế & phí được tính khi thanh toán"
                      : "Duties & Taxes calculated at checkout - choose to pay now or later"}
                  </span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => { setShowMarketPopup(false); setShowCountrySelector(true); }}
                  className="flex-1 border border-black px-6 py-3 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
                >
                  {locale === "vi" ? "Đổi quốc gia hoặc tiền tệ" : "Change Country or Currency"}
                </button>
                <button
                  onClick={dismissMarketPopup}
                  className="flex-1 bg-black text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-gray-900 transition-colors"
                >
                  {locale === "vi" ? "Mua ngay" : "Shop Now"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Country/Market Selector ─── */}
      {showCountrySelector && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]" onClick={() => { setShowCountrySelector(false); dismissMarketPopup(); }} />
          <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
            <div className="relative bg-white max-w-lg w-full shadow-2xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="font-display text-xl font-light tracking-normal">
                  {locale === "vi" ? "Chọn quốc gia" : "Select Your Country"}
                </h2>
                <button onClick={() => { setShowCountrySelector(false); dismissMarketPopup(); }} className="text-gray-400 hover:opacity-70">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto p-6 space-y-1">
                {(markets as { id: string; name: string; currency: string; countries: any; freeShippingThreshold: any }[])?.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => handleMarketChange(m.id)}
                    className={`w-full text-left px-4 py-3 rounded-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${market?.marketId === m.id ? "bg-gray-50 font-medium" : ""}`}
                  >
                    <span className="text-sm">{m.name}</span>
                    <span className="text-xs text-gray-500">{m.currency}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Single newsletter signup popup (Aritzia-style; Diolo Rewards retired 2026-09-02) ─── */}
      {showEmailPopup && !emailPopupDismissed && !previewAccess && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[200] transition-opacity"
            onClick={dismissEmailPopup}
          />
          <div className="fixed inset-0 z-[201] flex items-end sm:items-center justify-center">
            <div className="sf-email-popup relative bg-white w-full sm:max-w-sm sm:mx-4 shadow-2xl">
              <button
                onClick={dismissEmailPopup}
                className="absolute top-3 right-3 text-[#767676] hover:text-black z-10"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="px-6 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8">
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#767676] mb-3">
                  {locale === "en" ? "DIOLO 001" : "DIOLO 001"}
                </p>
                <h2 className="text-lg font-normal text-black tracking-tight mb-2">
                  {locale === "en" ? "Join the list" : "Nhận tin từ Diolo"}
                </h2>
                <p className="text-[13px] text-[#767676] leading-relaxed mb-6">
                  {locale === "en"
                    ? "New drops, Court Sessions and early access — the list hears first."
                    : "Drop mới, Court Session và quyền truy cập sớm — danh sách luôn biết trước."}
                </p>

                {newsletterSubmitted ? (
                  <div className="py-3 text-center">
                    <p className="text-sm font-normal text-black mb-1">
                      {locale === "en" ? "You're in." : "Đã đăng ký."}
                    </p>
                    <Link
                      to={localePath("/account/register", locale as Locale)}
                      className="text-xs text-black underline underline-offset-2 hover:opacity-70"
                      onClick={dismissEmailPopup}
                    >
                      {locale === "en" ? "Create your account →" : "Tạo tài khoản →"}
                    </Link>
                  </div>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (newsletterEmail.trim()) {
                        handleNewsletterSubmit(newsletterEmail, "rewards_popup");
                        setNewsletterSubmitted(true);
                      }
                    }}
                  >
                    <input
                      type="email"
                      placeholder={locale === "en" ? "Email address" : "Địa chỉ email"}
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      className="w-full border border-[#e0e0e0] bg-transparent px-4 py-3 text-[13px] focus:border-black focus:outline-none mb-3"
                    />
                    <button
                      type="submit"
                      className="w-full bg-black text-white py-3 text-xs uppercase tracking-[0.1em] font-normal hover:bg-[#1a1a1a] transition-colors"
                    >
                      {locale === "en" ? "Join Now" : "Tham gia ngay"}
                    </button>
                  </form>
                )}

                <button
                  onClick={dismissEmailPopup}
                  className="block w-full mt-3 text-[11px] text-[#767676] hover:text-black text-center transition-colors"
                >
                  {locale === "en" ? "No thanks" : "Không, cảm ơn"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Push Notification Prompt ─── */}
      <PushNotificationPrompt vapidPublicKey={vapidPublicKey as string | null} locale={locale} />

      {/* ─── Cart Drawer ─── always rendered for slide animation */}
      <div
        className={`sf-drawer-backdrop ${cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={closeCart}
        aria-hidden={!cartOpen}
      />
      <div
        className={`sf-drawer flex flex-col transition-transform duration-300 ease-out ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal={cartOpen ? true : undefined}
        aria-label="Shopping cart"
        aria-hidden={!cartOpen}
      >
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e0e0e0]">
              <h2 className="text-xs font-normal text-black uppercase tracking-[0.08em]">
                {t("cart", locale as Locale)} ({cartCount})
              </h2>
              <button
                onClick={closeCart}
                className="text-black hover:opacity-70"
                aria-label="Close cart"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Shipping Progress Bar (when cart has items) */}
            {cartItems.length > 0 && (
              <div className="px-5 py-3 bg-[#f4f4f4] border-b border-[#e0e0e0]">
                {hasFreeShipping ? (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-black flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs font-normal text-black">
                      {retailCustomer
                        ? t("freeShippingUnlocked", locale as Locale)
                        : (locale === "en" ? "You've unlocked free shipping!" : "Bạn đã được miễn phí vận chuyển!")}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-normal text-black mb-1.5">
                      {t("freeShippingProgress", locale as Locale, { amount: formatStorefrontPrice(shippingRemaining, currency as string) })}
                    </p>
                    <div className="h-[2px] bg-[#e0e0e0]">
                      <div
                        className="h-full bg-black transition-all duration-300"
                        style={{ width: `${shippingProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center text-center pt-8">
                  <svg className="w-10 h-10 text-[#999] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <p className="text-xs font-normal text-black mb-4">{t("emptyCart", locale as Locale)}</p>
                  <button
                    onClick={closeCart}
                    className="h-10 px-6 border border-black bg-white text-black text-xs font-normal hover:bg-black hover:text-white transition-colors mb-8 rounded-none"
                  >
                    {t("continueShopping", locale as Locale)}
                  </button>

                  {/* Empty Cart Recommendations */}
                  {recommendations.length > 0 && (
                    <div className="w-full text-left">
                      <h3 className="text-xs font-normal uppercase tracking-[0.08em] text-black mb-4 text-center">
                        {t("notSureWhereToStart", locale as Locale)}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {recommendations.slice(0, 4).map((product) => (
                          <Link
                            key={product.id}
                            to={localePath(`/products/${product.handle || product.id}`, locale as Locale)}
                            onClick={closeCart}
                            className="group/rec"
                          >
                            <div className="aspect-[1920/2623] bg-[#f5f5f5] overflow-hidden mb-2">
                              {product.featuredImageUrl ? (
                                <img
                                  src={fixImgUrl(product.featuredImageUrl)}
                                  alt={displayTitle(product.title, locale, (product as any).titleEn)}
                                  className="w-full h-full object-cover group-hover/rec:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full bg-[#f5f5f5]" />
                              )}
                            </div>
                            <p className="text-xs font-normal text-black line-clamp-1">{displayTitle(product.title, locale, (product as any).titleEn)}</p>
                            <p className="text-xs font-normal text-black mt-0.5">
                              {formatStorefrontPrice(Number(product.retailPrice || 0), currency as string)}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <ul className="space-y-5">
                    {cartItems.map((item) => (
                      <li key={item.variantId} className="flex gap-3">
                        <div className="w-[88px] h-[120px] bg-[#f5f5f5] flex-shrink-0 overflow-hidden relative">
                          {item.imageUrl && (
                            <img src={fixImgUrl(item.imageUrl)} alt={displayTitle(item.productTitle, locale, item.productTitleEn)} className="w-full h-full object-cover" />
                          )}
                          {(item as any).isPreOrder && (
                            <span className="absolute top-1 left-1 bg-black text-white px-1 py-0.5 text-[8px] font-normal uppercase tracking-[0.05em]">
                              {t("preOrder", locale)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={localePath(`/products/${item.productHandle}`, locale as Locale)}
                            className="text-xs font-normal text-black hover:underline line-clamp-2"
                            onClick={closeCart}
                          >
                            {displayTitle(item.productTitle, locale, item.productTitleEn)}
                          </Link>
                          <p className="text-[11px] text-[#767676] mt-1">
                            {item.color} / {item.size}
                          </p>
                          {/* Cart item: render in the item's stored currency
                              (set when user added it to the cart from a market
                              that may differ from the current one), falling
                              back to the active session currency. */}
                          <p className="text-xs font-normal text-black mt-1">{formatStorefrontPrice(item.price, item.currency || (currency as string))}</p>
                          <div className="flex items-center gap-3 mt-3">
                            <div className="flex items-center bg-[#f4f4f4]">
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                className="w-7 h-7 flex items-center justify-center text-black hover:bg-[#e0e0e0] transition-colors text-sm"
                                aria-label="Decrease quantity"
                              >
                                −
                              </button>
                              <span className="w-7 text-center text-xs font-normal tabular-nums">{item.quantity}</span>
                              {/* Disable + button when at the per-variant
                                  inventory cap captured at add-to-cart
                                  time. updateQuantity also clamps
                                  defensively so this is just a UX hint. */}
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                disabled={typeof item.maxQuantity === "number" && item.quantity >= item.maxQuantity}
                                className="w-7 h-7 flex items-center justify-center text-black hover:bg-[#e0e0e0] transition-colors text-sm disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-[#f4f4f4]"
                                aria-label="Increase quantity"
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.variantId)}
                              className="text-[11px] text-[#767676] hover:opacity-70 underline"
                            >
                              {t("remove", locale as Locale)}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* "You May Also Like" Recommendations (in non-empty cart) */}
                  {recommendations.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-[#e0e0e0]">
                      <h3 className="text-xs font-normal uppercase tracking-[0.08em] text-black mb-3">
                        {t("othersAlsoBought", locale as Locale)}
                      </h3>
                      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
                        {recommendations
                          .filter((r) => !cartItems.some((ci) => ci.productHandle === r.handle))
                          .slice(0, 6)
                          .map((product) => (
                            <Link
                              key={product.id}
                              to={localePath(`/products/${product.handle || product.id}`, locale as Locale)}
                              onClick={closeCart}
                              className="flex-none w-24 group/rec"
                            >
                              <div className="aspect-[1920/2623] bg-[#f5f5f5] overflow-hidden mb-1.5">
                                {product.featuredImageUrl ? (
                                  <img
                                    src={fixImgUrl(product.featuredImageUrl)}
                                    alt={displayTitle(product.title, locale, (product as any).titleEn)}
                                    className="w-full h-full object-cover group-hover/rec:scale-105 transition-transform duration-300"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-[#f5f5f5]" />
                                )}
                              </div>
                              <p className="text-[11px] font-normal text-black line-clamp-1">{displayTitle(product.title, locale, (product as any).titleEn)}</p>
                              <p className="text-[11px] font-normal text-black">
                                {formatStorefrontPrice(Number(product.retailPrice || 0), currency as string)}
                              </p>
                            </Link>
                          ))}
                      </div>
                      <div className="text-center mt-3">
                        <Link to={localePath("/products", locale as Locale)} onClick={closeCart} className="text-[11px] font-normal uppercase tracking-[0.08em] text-black underline hover:opacity-70">
                          {t("viewAllProducts", locale as Locale)}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            {cartItems.length > 0 && (
              <div className="border-t border-[#e0e0e0] pt-4 pb-5 px-5 mt-auto space-y-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-normal text-black uppercase tracking-[0.08em]">{t("subtotal", locale as Locale)}</span>
                  <span className="text-base font-normal text-black">{formatStorefrontPrice(cartSubtotal, currency as string)}</span>
                </div>
                <p className="text-[11px] text-[#767676]">
                  {hasFreeShipping
                    ? t("freeShippingLabel", locale as Locale)
                    : t("shippingAtCheckout", locale as Locale)}
                </p>
                <Link
                  to={localePath("/checkout", locale as Locale)}
                  onClick={closeCart}
                  className="block w-full h-12 bg-black text-white text-xs font-normal uppercase tracking-[0.08em] text-center leading-[3rem] hover:bg-[#1a1a1a] transition-colors rounded-none"
                >
                  {t("checkout", locale as Locale)}
                </Link>
                <Link
                  to={localePath("/cart", locale as Locale)}
                  onClick={closeCart}
                  className="block text-center text-[11px] text-[#767676] underline hover:opacity-70"
                >
                  {t("viewCart", locale as Locale)}
                </Link>
                {/* Security badge */}
                <div className="flex items-center justify-center gap-1.5 pt-2">
                  <svg className="w-3 h-3 text-[#999]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                  <span className="text-[10px] uppercase tracking-[0.08em] text-[#999]">Secure Checkout</span>
                </div>
              </div>
            )}
          </div>
    </div>
  );
}
